import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Users, Loader, Filter } from 'lucide-react';
import { contactService } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

const Contato = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedMembers, setGroupedMembers] = useState({});
  const [selectedCommission, setSelectedCommission] = useState('all');

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const { data, error } = await contactService.getTeamMembers();
      if (error) throw new Error(error);
      
      setTeamMembers(data || []);
      
      // Agrupar membros por comissão
      const grouped = (data || []).reduce((acc, member) => {
        const commission = member.commission || 'Outros';
        if (!acc[commission]) {
          acc[commission] = [];
        }
        acc[commission].push(member);
        return acc;
      }, {});
      
      setGroupedMembers(grouped);
    } catch (error) {
      toast.error('Erro ao carregar equipe organizadora');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatWhatsApp = (phone) => {
    if (!phone) return '';
    // Remove tudo que não é número
    const numbers = phone.replace(/\D/g, '');
    // Formata como (XX) XXXXX-XXXX
    if (numbers.length === 11) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7)}`;
    }
    return phone;
  };

  const openWhatsApp = (phone) => {
    if (!phone) return;
    const numbers = phone.replace(/\D/g, '');
    window.open(`https://wa.me/55${numbers}`, '_blank');
  };

  const sendEmail = (email) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  // Ordem das comissões
  const commissionOrder = [
    'COORDENAÇÃO GERAL',
    'COMISSÃO CIENTÍFICA',
    'COMISSÃO DE INFRAESTRUTURA',
    'COMISSÃO DE CREDENCIAMENTO',
    'COMISSÃO DE CULTURA'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amazonia-green-50 via-white to-golden-50 flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center">
          <Loader className="w-12 h-12 text-amazonia-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando equipe organizadora...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amazonia-green-50 via-white to-golden-50 py-12">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <Users className="w-12 h-12 text-amazonia-green-600 mr-3" />
            <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amazonia-green-600 to-golden-600">
              Equipe Organizadora
            </h1>
          </div>
        </motion.div>

        {/* Commission Filter */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 max-w-md mx-auto"
        >
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amazonia-green-600" />
            <select
              value={selectedCommission}
              onChange={(e) => setSelectedCommission(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border-2 border-amazonia-green-200 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-amazonia-green-500 focus:border-transparent text-amazonia-dark-800 font-medium appearance-none cursor-pointer hover:border-amazonia-green-300 transition-colors"
            >
              <option value="all">Todas as Comissões</option>
              {commissionOrder.map((commission) => (
                <option key={commission} value={commission}>
                  {commission}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-amazonia-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </motion.div>

        {/* Team Members by Commission */}
        {commissionOrder
          .filter((commissionName) => selectedCommission === 'all' || selectedCommission === commissionName)
          .map((commissionName, index) => {
          const members = groupedMembers[commissionName];
          if (!members || members.length === 0) return null;

          return (
            <motion.div
              key={commissionName}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="mb-12"
            >
              {/* Commission Title */}
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-amazonia-dark-800 border-l-4 border-golden-500 pl-4">
                  {commissionName}
                </h2>
              </div>

              {/* Members Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map((member, memberIndex) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 + memberIndex * 0.05 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    {/* Photo */}
                    <div className="relative w-full aspect-square bg-gradient-to-br from-amazonia-green-100 to-golden-100 flex items-center justify-center overflow-hidden">
                      {member.photo_url ? (
                        <img
                          src={member.photo_url}
                          alt={member.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div class="w-24 h-24 rounded-full bg-gradient-to-br from-amazonia-green-500 to-golden-500 flex items-center justify-center">
                                <span class="text-4xl font-bold text-white">${member.name.charAt(0)}</span>
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amazonia-green-500 to-golden-500 flex items-center justify-center shadow-lg">
                          <span className="text-4xl font-bold text-white">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-amazonia-dark-800 mb-4 text-center">
                        {member.name}
                      </h3>

                      {/* Contact Buttons */}
                      <div className="space-y-3">
                        {member.whatsapp && (
                          <button
                            onClick={() => openWhatsApp(member.whatsapp)}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                          >
                            <Phone className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              Fale comigo
                            </span>
                          </button>
                        )}

                        {member.email && (
                          <button
                            onClick={() => sendEmail(member.email)}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                          >
                            <Mail className="w-4 h-4" />
                            <span className="text-sm font-medium break-all">
                              {member.email}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          );
        })}

        {/* Empty State */}
        {teamMembers.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              Nenhum membro da equipe cadastrado ainda.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Contato;
